import { useState } from 'react';
import { NavLink, generatePath, useParams } from 'react-router-dom';
import {
  IconUp,
  IconDown,
  IconDownload,
  formatDate,
  IconLoader,
} from '@ui/src';
import { useLazyGetCustomerAgreementFileQuery } from '../store/services/customers';
import { CustomerAgreement, CustomerType } from '../types';
import getAgreementTypeName from '../../new-application/helpers/get-agreement-type-name';
import { AgreementItemTypes } from './types';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';

const AgreementItem = ({
  item,
  earlyVersions,
  customerType = CustomerType.INDIVIDUAL,
}: AgreementItemTypes) => {
  const [versionsVisible, setVersionsVisible] = useState(false);
  const [downloadedFileId, setDownloadedFileId] = useState('');

  const { customerId } = useParams();

  const [
    getAgreementFile,
    { isLoading: downloadInProgress, isFetching: downloadIsFetching },
  ] = useLazyGetCustomerAgreementFileQuery();

  const getAgreementFileAction = async (agreement: CustomerAgreement) => {
    setDownloadedFileId(agreement.id);

    const response = await getAgreementFile(agreement.id);

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${getAgreementTypeName(
        agreement?.type,
      )}(v${agreement?.version})`;
      hiddenElement.click();

      setDownloadedFileId('');
    }
  };

  const link = generatePath(
    customerType === CustomerType.INDIVIDUAL
      ? ROUTES.CUSTOMERS_INDIVIDUAL_AGREEMENT
      : ROUTES.CUSTOMERS_CORPORATE_AGREEMENT,
    { customerId: (customerId as string) || '', agreementId: item.id },
  );

  return (
    <>
      <div className={cn.agreementItem}>
        <div className={cn.agreementValue}>
          <NavLink className={cn.agreementLink} to={link} />
          {getAgreementTypeName(item.type)}
        </div>
        <div className={cn.agreementValue}>{item.version}</div>
        <div className={cn.agreementValue}>
          <div>
            <span>
              {item.signatures && item.signatures.edges.length > 0
                ? formatDate(item.signatures.edges[0].node.signedAt)
                : '-'}
            </span>
            <div className={cn.agreementValueControls}>
              {earlyVersions && earlyVersions?.length > 0 && (
                <button
                  type="button"
                  className={cn.showEarlyVersionsButton}
                  onClick={() => setVersionsVisible(!versionsVisible)}
                >
                  earlier versions
                  {versionsVisible ? <IconUp /> : <IconDown />}
                </button>
              )}
              <button
                type="button"
                aria-label="Agreement link"
                className={cn.downloadAgreementButton}
                onClick={() => getAgreementFileAction(item)}
              >
                {(downloadInProgress || downloadIsFetching) &&
                downloadedFileId === item.id ? (
                  <IconLoader width={16} height={16} />
                ) : (
                  <IconDownload />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {versionsVisible &&
        earlyVersions?.map((agreement) => {
          const earlyVersionLink = generatePath(
            customerType === CustomerType.INDIVIDUAL
              ? ROUTES.CUSTOMERS_INDIVIDUAL_AGREEMENT
              : ROUTES.CUSTOMERS_CORPORATE_AGREEMENT,
            {
              customerId: (customerId as string) || '',
              agreementId: agreement.id,
            },
          );

          return (
            <div
              className={`${cn.agreementItem} ${cn.earlyAgreementItem}`}
              key={agreement.id}
            >
              <div className={`${cn.agreementValue} ${cn.earlyAgreementValue}`}>
                <NavLink className={cn.agreementLink} to={earlyVersionLink} />
                {getAgreementTypeName(agreement.type)}
              </div>
              <div className={`${cn.agreementValue} ${cn.earlyAgreementValue}`}>
                {agreement.version}
              </div>
              <div className={`${cn.agreementValue} ${cn.earlyAgreementValue}`}>
                <div>
                  <span>
                    {agreement.signatures &&
                    agreement.signatures.edges.length > 0
                      ? formatDate(
                          agreement.signatures.edges[0]?.node?.signedAt,
                        )
                      : '-'}
                  </span>
                  <div className={cn.agreementValueControls}>
                    <button
                      type="button"
                      aria-label="Agreement link"
                      className={cn.downloadAgreementButton}
                      onClick={() => getAgreementFileAction(agreement)}
                    >
                      {(downloadInProgress || downloadIsFetching) &&
                      downloadedFileId === agreement.id ? (
                        <IconLoader width={16} height={16} />
                      ) : (
                        <IconDownload />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </>
  );
};

export default AgreementItem;
